// Which brand a request is rendered as, decided by the host it arrived on.
//
// VoiceFinder is shared by partners that sell under their own name. A brand only
// changes the chrome around the talent search - logo, colours, the absolute URLs
// (canonicals, hreflang, share links, sitemap), the company's own links and e-mail
// addresses, and which locales the host serves. The talent data is the same.
//
// Asset URLs and colours mirror the portal's per-host settings
// (systemet: Application/settings/{hostname}.json -> logo*, favicon, brand.colors).
//
// Precedence: on a project page the project's own brand (ProjectContext) still wins
// over the host brand, so a project shared from one host keeps its branding on the
// other.

export const DEFAULT_BRAND = 'voicearchive'

// Every host not listed here is VoiceArchive: www.voicearchive.com (proxied),
// voicefinder.voicearchive.com, beta.voicefinder…, share.voice-selector.com, localhost.
export const BRAND_BY_HOST = {
  'voicefinder.voicetome.com': 'voicetome',
}

// A rewrite per VoiceArchive page, matching the page with or without www, a leading
// /en/ and a trailing slash - and nothing longer, so /services does not eat
// /services-something.
const pageRewrites = (pages, base = 'https://eng.voicetome.com/') =>
  Object.entries(pages).map(([from, to]) => [
    new RegExp(
      `https?://(www\\.)?voicearchive\\.com/(en/)?${from}/?(?=[\\s"'<>)]|$)`,
      'g'
    ),
    base + to,
  ])

export const brands = {
  voicearchive: {
    key: 'voicearchive',
    name: 'VoiceArchive',
    // The portal partner a project created here belongs to (systemet Partner::HQ).
    partnerId: 0,
    theme: 'base',
    canonicalBase: 'https://www.voicearchive.com/',
    shareBase: 'https://share.voice-selector.com/',
    // Where "Log in" sends people (the portal's /user/preauthenticate). null = the API
    // host (RAZZLE_API_BASE). The portal only hands the token back to an origin in
    // that host's allowedCorsOrigins.
    loginBase: null,
    // null = the inline VoiceArchive SVG (components/Logo/Svg.js)
    logo: null,
    // null = /favicon.png served by this app
    favicon: null,
    ogImage:
      'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/icon-blue.png',
    projectOgImage:
      'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/voicefinder-project-og-image.png',
    siteName: 'VoiceArchive',
    twitterHandle: '@VoiceArchive',
    gtmId: 'GTM-5468Z3N',
    contactEmail: 'mail@voicearchive.com',
    // Listed explicitly so a locale added for another brand is not served (or put in the
    // sitemap and hreflang) here. Must be keys of supportedLocales (src/i18n.js).
    locales: ['en', 'en-gb', 'en-za', 'de-de', 'da-dk'],
    // Applied to every message and to the SEO copy. VoiceArchive's copy is the original.
    rewrites: [],
  },
  voicetome: {
    key: 'voicetome',
    name: 'VoiceToMe',
    partnerId: 3,
    theme: 'vtm',
    canonicalBase: 'https://voicefinder.voicetome.com/',
    shareBase: 'https://voicefinder.voicetome.com/',
    // Same server as my.voicearchive.com, but their own branded sign-in page - their
    // staff already log in there.
    loginBase: 'https://system.voicetome.com',
    logo: {
      src: 'https://voicetome-branding.s3.eu-central-1.amazonaws.com/logo-white-white.png',
      alt: 'VoiceToMe',
    },
    favicon:
      'https://voicetome-branding.s3.eu-central-1.amazonaws.com/icon.png',
    // TODO: VoiceToMe has no dedicated share images yet; the icon is what the portal uses.
    ogImage:
      'https://voicetome-branding.s3.eu-central-1.amazonaws.com/icon.png',
    projectOgImage:
      'https://voicetome-branding.s3.eu-central-1.amazonaws.com/icon.png',
    siteName: 'VoiceToMe',
    twitterHandle: null,
    // TODO: VoiceToMe's own container, if they want analytics. Never VoiceArchive's.
    gtmId: null,
    contactEmail: 'info@voicetome.com',
    // sv-se and nb-no fall back to English until VoiceToMe's copy is pulled from the sheet.
    locales: ['en', 'sv-se', 'nb-no'],
    rewrites: [
      // VoiceArchive pages -> VoiceToMe's own equivalent on eng.voicetome.com (checked
      // against their sitemap 2026-10-12). Pages they have no equivalent for (terms,
      // pricing, cases, about, the AI page) open their homepage, via the catch-all.
      ...pageRewrites({
        contact: 'contact-us/',
        faq: 'faq/',
        'privacy-policy': 'privacy-policy/',
        'apply-voice-talent': 'application-information/',
        services: 'services/',
        'live-sessions': 'service/live-sessions-direction/',
        'audio-post-services': 'service/audio-production/',
        'voice-casting-and-auditions': 'service/casting-2/',
        'advertising-voice-over': 'service/advertising-voice/',
        'line-of-business': 'services/',
        'elearning-voice-over': 'services/',
        'video-voice-over': 'services/',
        'ivr-voice-over': 'services/',
        'gaming-voice-over': 'services/',
        'products-technology-voice-over': 'services/',
      }),
      [
        /https?:\/\/(www\.)?voicearchive\.com(\/[^\s"'<>)]*)?/g,
        'https://eng.voicetome.com/',
      ],
      [/[\w.+-]+@voicearchive\.com/g, 'info@voicetome.com'],
      // partnerConf[3].phone in the portal
      [/\+45 89 87 30 03/g, '+46 36-770 15 40'],
      [/VoiceArchives\b/g, "VoiceToMe's"],
      [/VoiceArchive/g, 'VoiceToMe'],
    ],
  },
}

export const normaliseHost = (raw = '') =>
  String(raw || '')
    .split(',')[0]
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, '')

export const resolveBrandKey = (rawHost) =>
  BRAND_BY_HOST[normaliseHost(rawHost)] || DEFAULT_BRAND

// The public hostnames VoiceFinder is served on. A request whose Host is one of these
// is branded by it, and X-Forwarded-Host is ignored: anyone can send that header, and a
// cache in front would otherwise store a page branded for the wrong host.
export const PUBLIC_HOSTS = [
  'www.voicearchive.com',
  'voicearchive.com',
  'voicefinder.voicearchive.com',
  'beta.voicefinder.voicearchive.com',
  'share.voice-selector.com',
  'www.voice-selector.com',
  'voice-selector.com',
  ...Object.keys(BRAND_BY_HOST),
]

// X-Forwarded-Host is only consulted when the proxy replaced Host with an internal name.
export const resolveRequestBrandKey = (host, forwardedHost) =>
  PUBLIC_HOSTS.includes(normaliseHost(host))
    ? resolveBrandKey(host)
    : resolveBrandKey(forwardedHost || host)

export const getBrand = (key) => brands[key] || brands[DEFAULT_BRAND]

// The brand's locale a request should fall back to when it asks for one the brand does
// not serve.
export const defaultBrandLocale = (brand, defaultLocale) =>
  brand.locales.includes(defaultLocale) ? defaultLocale : brand.locales[0]

export default brands
